import m from 'mithril';
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import avatar from 'flarum/common/helpers/avatar';
import Stream from 'flarum/common/utils/Stream';
import CommentPost from 'flarum/forum/components/CommentPost';
import ReplyPlaceholder from 'flarum/forum/components/ReplyPlaceholder';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
// Akses DOM ke quick reply (dipakai reroute agar tidak perlu state global).
function quickReplyAPI() {
  const box = document.querySelector('.RvsrQuickReply');
  if (!box || !app.session.user) return null;
  const ta = box.querySelector('textarea');
  if (!ta) return null;
  return {
    box,
    ta,
    get: () => ta.value,
    set: (v) => {
      ta.value = v;
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    },
    insertAtCursor(text) {
      const s = ta.selectionStart ?? ta.value.length;
      const e = ta.selectionEnd ?? ta.value.length;
      ta.value = ta.value.slice(0, s) + text + ta.value.slice(e);
      const pos = s + text.length;
      try {
        ta.setSelectionRange(pos, pos);
      } catch (_) {
        // abaikan
      }
      ta.dispatchEvent(new Event('input', { bubbles: true }));
    },
    focus() {
      box.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => {
        try {
          ta.focus({ preventScroll: true });
        } catch (_) {
          ta.focus();
        }
      }, 350);
    },
  };
}

// Shim composer minimal agar rantai mentions reply() (insertMention)
// tetap jalan, tapi tulisannya masuk ke quick reply, bukan popup.
function shimComposer(api) {
  return {
    fields: {
      content(v) {
        if (v === undefined) return api.get();
        api.set(v);
      },
    },
    editor: {
      getSelectionRange: () => {
        const s = api.ta.selectionStart || 0;
        const e = api.ta.selectionEnd || 0;
        return [s, e];
      },
      insertAtCursor: (text) => {
        // Mentions menyisipkan @"user"#pN; ubah jadi quote penuh ala BF.
        const mm = /#p(\d+)/.exec(text || '');
        if (mm) {
          const p = app.store.getById('posts', mm[1]);
          if (p) {
            api.insertAtCursor(buildQuote(p));
            return;
          }
        }
        api.insertAtCursor(text);
      },
    },
    body: { attrs: {} },
  };
}

 // Syntax mention post persis core: @"DisplayName"#p<postId>.
function postMention(post) {
  try {
    if (app.mentionFormats) {
      const mentionable = app.mentionFormats.mentionable('post');
      if (mentionable) return mentionable.replacement(post);
    }
  } catch (_) {
    // abaikan, pakai fallback manual
  }
  try {
    const u = post.user ? post.user() : null;
    const name = u ? u.displayName() || u.username() : '?';
    return `@"${name}"#p${post.id()}`;
  } catch (_) {
    return '';
  }
}

// Mount quick reply (scroll progresif) lalu sisip quote full post.
function ensureBox() {
  const api = quickReplyAPI();
  if (api) return Promise.resolve(api);
  const jumpBottom = () => {
    try {
      window.scrollTo(0, document.body.scrollHeight);
    } catch (_) {
      // abaikan
    }
  };
  jumpBottom();
  return new Promise((res) => {
    let tries = 0;
    const tick = () => {
      const found = quickReplyAPI();
      if (found || ++tries > 60) return res(found);
      if (tries % 4 === 0) jumpBottom();
      setTimeout(tick, 250);
    };
    setTimeout(tick, 300);
  });
}

// Bangun blockquote markdown dari sebuah post (mention siapa + teks dikutip).
function buildQuote(post) {
  const mention = postMention(post);
  let raw = '';
  try {
    const html = post.contentHtml ? post.contentHtml() : '';
    if (html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      raw = (tmp.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
    }
  } catch (_) {
    raw = '';
  }
  if (raw.length > 800) raw = raw.slice(0, 800).trim() + '…';
  return raw ? '> ' + mention + '\n> ' + raw.replace(/\n/g, '\n> ') + '\n\n' : mention + ' ';
}

 // Quick reply inline ala BF: ketik + Post Reply langsung di thread,
 // tanpa popup composer. Composer penuh tetap ada via "Editor lengkap".
const QuickReply = {
  oninit(vnode) {
    this.discussion = vnode.attrs.discussion;
    this.content = Stream('');
    this.loading = false;
    this.uploading = false;
    this.error = null;
    this.ta = null;
  },

  getTa() {
    if (this.ta && document.body.contains(this.ta)) return this.ta;
    const box = document.querySelector('.RvsrQuickReply');
    this.ta = box ? box.querySelector('textarea') : null;
    return this.ta;
  },

  // Bungkus teks terpilih (bold/italic/code/link).
  surround(before, after) {
    const ta = this.getTa();
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const val = ta.value;
    const sel = val.slice(s, e);
    this.content(val.slice(0, s) + before + sel + after + val.slice(e));
    m.redraw();
    requestAnimationFrame(() => {
      ta.focus();
      const pos = s + before.length + sel.length;
      ta.setSelectionRange(pos, pos);
    });
  },

  insertText(text) {
    const ta = this.getTa();
    if (!ta) {
      this.content(this.content() + text);
      m.redraw();
      return;
    }
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const val = ta.value;
    this.content(val.slice(0, s) + text + val.slice(e));
    m.redraw();
    requestAnimationFrame(() => {
      ta.focus();
      const pos = s + text.length;
      ta.setSelectionRange(pos, pos);
    });
  },

  insertImage() {
    const url = window.prompt('Image URL');
    if (url) this.insertText('![](' + url.trim() + ')');
  },

  // Upload file lewat API fof/upload lalu sisip markdown yang dikembalikan.
  uploadFiles(files) {
    if (!files || !files.length || this.uploading) return;
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append('files[]', files[i]);
    this.uploading = true;
    this.error = null;
    m.redraw();
    app
      .request({ method: 'POST', url: app.forum.attribute('apiUrl') + '/fof/upload', serialize: (t) => t, body: fd })
      .then((res) => {
        const data = (res && res.data) || [];
        const md = data
          .map((f) => (f.attributes && (f.attributes.bbcode || f.attributes.markdown)) || '')
          .filter(Boolean)
          .join('\n');
        this.uploading = false;
        if (md) {
          const cur = this.content();
          this.insertText((cur && !cur.endsWith('\n') ? '\n' : '') + md + '\n');
        } else {
          m.redraw();
        }
      })
      .catch((e) => {
        this.uploading = false;
        let detail = 'Upload failed. Try again.';
        try {
          const errs = e && e.response && e.response.errors;
          if (errs && errs[0] && errs[0].detail) detail = errs[0].detail;
        } catch (_) {
          // pakai pesan default
        }
        this.error = detail;
        m.redraw();
      });
  },

  submit(e) {
    if (e) e.preventDefault();
    const content = this.content().trim();
    if (!content || this.loading) return;
    this.loading = true;
    this.error = null;
    m.redraw();

    app.store
      .createRecord('posts')
      .save({ content, relationships: { discussion: this.discussion } })
      .then(
        (post) => {
          this.loading = false;
          this.content('');
          m.redraw();
          if (app.viewingDiscussion(this.discussion)) {
            const stream = app.current.get('stream');
            stream.update().then(() => stream.goToNumber(post.number()));
          }
        },
        () => {
          this.loading = false;
          this.error = 'Failed to post reply. Try again.';
          m.redraw();
        }
      );
  },

  fullEditor(e) {
    if (e) e.preventDefault();
    app.composer.load(ReplyComposer, { user: app.session.user, discussion: this.discussion });
    app.composer.show();
    try {
      app.composer.fields.content(this.content());
    } catch (_) {
      // composer belum siap — user ketik ulang di sana
    }
  },

  toolBtn(icon, title, onclick) {
    return (
      <button type="button" className="RvsrQR-tool" title={title} onclick={onclick}>
        <i className={'icon fas fa-' + icon} />
      </button>
    );
  },

  view() {
    const user = app.session.user;
    const ready = this.content().trim().length > 0 && !this.loading;
    return (
      <form className="RvsrQuickReply" onsubmit={(e) => this.submit(e)}>
        <div className="RvsrQuickReply-bar">
          <span className="RvsrQuickReply-title">Quick reply</span>
          <button type="button" className="RvsrQuickReply-full" onclick={(e) => this.fullEditor(e)}>
            Full editor
          </button>
        </div>
        <div className="RvsrQuickReply-main">
          {user ? avatar(user, { className: 'RvsrQuickReply-avatar', loading: 'lazy' }) : null}
          <div className="RvsrQuickReply-field">
            <div className="RvsrQuickReply-tools">
              {this.toolBtn('bold', 'Bold', () => this.surround('**', '**'))}
              {this.toolBtn('italic', 'Italic', () => this.surround('_', '_'))}
              {this.toolBtn('code', 'Code', () => this.surround('`', '`'))}
              {this.toolBtn('quote-right', 'Quote', () => this.surround('> ', ''))}
              {this.toolBtn('link', 'Link', () => this.surround('[', '](url)'))}
              {this.toolBtn('image', 'Image URL', () => this.insertImage())}
              <label className="RvsrQR-tool RvsrQR-upload" title="Upload file">
                <i className={'icon fas ' + (this.uploading ? 'fa-spinner fa-spin' : 'fa-paperclip')} />
                <input
                  type="file"
                  multiple
                  onchange={(e) => {
                    this.uploadFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <textarea
              className="FormControl RvsrQuickReply-input"
              rows="3"
              placeholder="Write a reply… (Markdown supported)"
              value={this.content()}
              oninput={(e) => this.content(e.target.value)}
              onkeydown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') this.submit(e);
              }}
              disabled={this.loading}
            />
          </div>
        </div>
        {this.error ? <div className="RvsrQuickReply-error">{this.error}</div> : null}
        <div className="RvsrQuickReply-foot">
          <span className="RvsrQuickReply-hint">Ctrl+Enter to send</span>
          <Button type="submit" className="Button Button--primary" loading={this.loading} disabled={!ready}>
            Post Reply
          </Button>
        </div>
      </form>
    );
  },
};

export default function addInlineReply() {
  // Ganti tombol placeholder dengan form inline. Mode preview (saat
  // composer popup dipakai) tetap pakai tampilan bawaan.
  override(ReplyPlaceholder.prototype, 'view', function (original) {
    if (!app.session.user) return original();
    if (app.composer.composingReplyTo(this.attrs.discussion)) return original();
    return <QuickReply discussion={this.attrs.discussion} key={'qr-' + this.attrs.discussion.id()} />;
  });

  // Belokkan SEMUA pemicu reply (sidebar + tombol Reply per post +
  // mention quote) ke quick reply inline. Quick reply hanya mount
  // setelah stream mentok bawah — paksa mount via scroll dulu, lalu
  // fokus. Tanpa quick reply (mis. tidak bisa reply) fallback bawaan.
  const origReplyAction = DiscussionControls.replyAction;
  DiscussionControls.replyAction = function (goToLast, forceRefresh) {
    const discussion = this;
    try {
      if (discussion && app.viewingDiscussion(discussion)) {
        return ensureBox().then((api) => {
          if (!api) return origReplyAction.call(discussion, goToLast, forceRefresh);
          api.focus();
          return shimComposer(api);
        });
      }
    } catch (_) {
      // jatuh ke composer bawaan
    }
    return origReplyAction.call(discussion, goToLast, forceRefresh);
  };
  // Tombol Reply per post tetap milik mentions; quote penuh disisipkan oleh
  // shimComposer.insertAtCursor (semua jalur reply lewat sana).
}
